class Logger {
    // Singleton instance of Logger
    static shared = new Logger();
    
    constructor() {
        // Initialize environment variables
        this.environment = process.env.REACT_APP_ENVIRONMENT || 'dev';
        this.logsOperational = process.env.REACT_APP_LOGS_OPERATIONAL === 'true';
        this.infoOperational = process.env.REACT_APP_INFO_OPERATIONAL === 'true';
        
        // Log batching and file management
        this.logBuffer = '';
        this.bufferSize = 8192; // 8KB buffer size
        // Stay under typical localStorage quota (~5MB). Trigger upload before this; trim if we hit quota.
        this.maxStorageBytes = 4 * 1024 * 1024; // 4MB
        this.lastFlushTime = Date.now();
        this.flushInterval = 5000; // 5 seconds
        this.isUploading = false;
        this.isUploadScheduled = false;
        this.tempLogCache = '';
        this.uploadRetryCount = 0;
        this.maxRetries = 3;
        
        // Environment URL for CloudWatch uploads
        this.environmentURL = null;
        
        // Auth tokens for API calls (same as iOS app)
        this.authTokens = null;
        
        // Store user ID separately for logging
        this.userId = null;
        
        // Buffer for pending logs
        this.pendingLogs = [];
        this.maxPendingLogs = 100; // Limit to prevent memory issues
        
        // New log attributes
        this.sessionId = null; // Will be generated on login
        this.transactionId = this.generateUUID(); // Generated when file changes
        this.deviceId = ''; // Empty string as requested
        this.currentFile = null; // Track current file for transaction ID generation
        this.fileVisitCount = {}; // Track visit count for each file
        
        // Initialize log file and start flush timer
        this.initializeLogFile();
        this.startFlushTimer();
        this.initializeLifecycleHandlers();

        // Expose for manual inspection during debugging
        try { if (typeof window !== 'undefined') { window.__PHLogger = this; } } catch (_) {}
    }

    // MARK: - UUID and Device ID Management

    generateUUID() {
        // Use built-in crypto.randomUUID() for secure, RFC 4122 compliant UUIDs
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        
        // Fallback for older browsers that don't support crypto.randomUUID()
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }


    generateNewTransactionId() {
        // Generate new transaction ID (now used when file changes)
        this.transactionId = this.generateUUID();
        return this.transactionId;
    }

    generateNewSessionId() {
        // Generate new session ID (call this on login)
        this.sessionId = this.generateUUID();
        return this.sessionId;
    }

    // MARK: - Public Logging Methods

    debug(message, customMessage = null, fileName = null) {
        console.log(message);
        const logEntry = this.createLogEntry('DEBUG', message, customMessage, fileName);
        this.sendToCloudWatch(logEntry);
    }

    info(element, action, fileName = null, customMessage = null) {
        if (this.infoOperational) {
            // Print call stack for debugging. Call stack doesnt show individual files, just "bundle.js"
            // console.log("Call Stack: " + new Error().stack);
            
            
            const message = this.formatLogMessage(element, action, customMessage);
            const logEntry = this.createLogEntry('INFO', message, customMessage, fileName);
            console.log('INFO:', logEntry);
            this.sendToCloudWatch(logEntry);
        }
    }

    error(message, customMessage = null, fileName = null) {
        console.error(message);
        const logEntry = this.createLogEntry('ERROR', message, customMessage, fileName);
        this.sendToCloudWatch(logEntry);
    }

    warn(message, customMessage = null, fileName = null) {
        console.warn(message);
        const logEntry = this.createLogEntry('WARN', message, customMessage, fileName);
        this.sendToCloudWatch(logEntry);
    }

    // MARK: - Private Methods


    shouldLog() {
        // Determine if logging should occur based on operational status and user account ID
        if (!this.logsOperational) {
            return false;
        }
        
        // For development/testing, allow logging without user ID
        if (this.environment === 'dev' || this.environment === 'local') {
            return true;
        }
        
        // Get user account ID from Redux store or localStorage
        const userAccountId = this.getUserAccountId();
        return userAccountId && userAccountId !== 0;
    }

    getUserAccountId() {
        try {
            // First try to get from Logger's stored userId
            if (this.userId) {
                return this.userId;
            }
            
            // Fallback: try to get from localStorage
            const userInfo = localStorage.getItem('userInfo');
            
            if (userInfo) {
                const parsed = JSON.parse(userInfo);
                return parsed.id;
            }
            return null;
        } catch (error) {
            console.error('Error getting user account ID:', error);
            return null;
        }
    }



    // MARK: - Log Batching and File Management

    initializeLogFile() {
        try { localStorage.getItem('perinHealthLogs') ?? localStorage.setItem('perinHealthLogs', ''); }
        catch (error) { console.error('Error initializing log file:', error); }
    }

    startFlushTimer() {
        // Flush buffer every 5 seconds
        setInterval(() => {
            this.flushBuffer();
        }, this.flushInterval);
        
        // Also trigger upload every 30 seconds to ensure logs are sent
        setInterval(() => {
            try {
                const hasBufferedLogs = this.logBuffer.length > 0;
                const hasPersistedLogs = (this.getLogFileContent() || '').trim().length > 0;
                if (hasBufferedLogs || hasPersistedLogs) {
                    this.uploadLogFile();
                }
            } catch (_) {}

        }, 30000);
    }

    flushBuffer() {
        if (this.logBuffer.length > 0) {
            this.writeLogToFile(this.logBuffer);
            this.logBuffer = '';
            this.lastFlushTime = Date.now();
        }
    }

    writeLogToFile(logContent) {
        try {
            if (this.isUploading) {
                // If uploading, add to temporary cache
                this.tempLogCache += logContent;
                return;
            }

            let existingLogs = localStorage.getItem('perinHealthLogs') || '';
            const newSize = new Blob([existingLogs + logContent]).size;
            // Keep under localStorage quota. trim oldest if needed
            if (newSize > this.maxStorageBytes) {
                const targetBytes = Math.max(0, this.maxStorageBytes - new Blob([logContent]).size - 1024);
                if (targetBytes > 0 && existingLogs.length > 0) {
                    existingLogs = this._trimLogContentToBytes(existingLogs, targetBytes);
                } else {
                    existingLogs = '';
                }
            }
            const newLogs = existingLogs + logContent;
            try {
                localStorage.setItem('perinHealthLogs', newLogs);
            } catch (quotaError) {
                if (quotaError?.name === 'QuotaExceededError') {
                    // Trim to tail and retry with just new content if necessary
                    const trimmed = this._trimLogContentToBytes(newLogs, this.maxStorageBytes - 1024);
                    localStorage.setItem('perinHealthLogs', trimmed);
                } else {
                    throw quotaError;
                }
            }

            // Check if file size exceeds threshold and trigger upload
            if (this.isFileExceedingMaxSize()) {
                this.uploadLogFile();
            }
        } catch (error) {
            console.error('❌ Error writing to log file:', error);
        }
    }

    // Keep the tail of content within roughly maxBytes (by line to avoid cutting mid-line)
    _trimLogContentToBytes(content, maxBytes) {
        if (maxBytes <= 0 || content.length === 0) return '';
        const blob = new Blob([content]);
        if (blob.size <= maxBytes) return content;
        const lines = content.split('\n');
        let tail = '';
        for (let i = lines.length - 1; i >= 0; i--) {
            const candidate = (lines[i] + (i < lines.length - 1 ? '\n' : '')) + tail;
            if (new Blob([candidate]).size > maxBytes) break;
            tail = candidate;
        }
        return tail || lines[lines.length - 1] || '';
    }

    isFileExceedingMaxSize() {
        try {
            const logs = localStorage.getItem('perinHealthLogs') || '';
            const sizeInBytes = new Blob([logs]).size;
            return sizeInBytes > this.maxStorageBytes;
        } catch (error) {
            console.error('Error checking file size:', error);
            return false;
        }
    }

    getLogFileContent() {
        try { return localStorage.getItem('perinHealthLogs') || ''; }
        catch (error) { console.error('Error getting log file content:', error); return ''; }
    }

    clearLogFile() {
        try { localStorage.setItem('perinHealthLogs', ''); }
        catch (error) { console.error('Error clearing log file:', error); }
    }

    initializeLifecycleHandlers() {
        // Ensure we don't lose logs when the tab is closed or backgrounded
        try {
            window.addEventListener('beforeunload', () => {
                try { this.flushBuffer(); } catch (_) {}
                try { this.bestEffortUploadOnUnload(); } catch (_) {}
            });
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    try { this.flushBuffer(); } catch (_) {}
                    try {
                        const hasPersistedLogs = (this.getLogFileContent() || '').trim().length > 0;
                        if (hasPersistedLogs) {
                            this.uploadLogFile();
                        }
                    } catch (_) {}
                    try { this.bestEffortUploadOnUnload(); } catch (_) {}
                }
            });
        } catch (_) {
            // No-op if not running in a browser environment
        }
    }

    async bestEffortUploadOnUnload() {
        try {
            const envURL = this.getEnvironmentURL();
            if (!envURL) { return; }
            const logContent = this.getLogFileContent();
            if (!logContent || logContent.trim() === '') { return; }

            const uploadURL = `${envURL.replace(/\/$/, '')}/upload/logs`;
            const formData = new FormData();
            const logBlob = new Blob([logContent], { type: 'text/plain' });
            formData.append('logFile', logBlob, 'WebAppLogs.txt');
            formData.append('os', 'web');

            await fetch(uploadURL, {
                method: 'POST',
                headers: {
                    ...this.getAuthHeaders(),
                },
                body: formData,
                keepalive: true,
            }).catch(() => {});
        } catch (error) {
            console.error('Error in bestEffortUploadOnUnload:', error);
        }
    }

    formatLogMessage(element, action, customMessage = null) {
        const baseMessage = `${element} ${action}`;
        return customMessage ? `${baseMessage} (${customMessage})` : baseMessage;
    }

    formatCurrentDate() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
        
        // Get timezone offset in format like -0700 (matching iOS)
        const timezoneOffset = now.getTimezoneOffset();
        const offsetHours = Math.abs(Math.floor(timezoneOffset / 60));
        const offsetMinutes = Math.abs(timezoneOffset % 60);
        const offsetSign = timezoneOffset > 0 ? '-' : '+';
        const timezone = `${offsetSign}${String(offsetHours).padStart(2, '0')}${String(offsetMinutes).padStart(2, '0')}`;
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds} ${timezone}`;
    }

    createLogEntry(type, message, customMessage = null, fileName = null) {
        // Get the calling function name for method (same as iOS app)
        const stack = new Error().stack;
        const method = this.extractMethodFromStack(stack);
        
        const finalFileName = fileName || 'unknown.js';
        
        // Check if file has changed and generate new transaction ID if needed
        if (this.currentFile !== finalFileName) {
            this.currentFile = finalFileName;
            // Increment visit count for this file
            this.fileVisitCount[finalFileName] = (this.fileVisitCount[finalFileName] || 0) + 1;
            this.transactionId = this.generateUUID();
        }
        
        const timestamp = this.formatCurrentDate();
        const userAccountId = this.getUserAccountId() || 0;
        
        // Generate individual log ID for this specific log entry
        const logId = this.generateUUID();
        
        // Use session ID if available, otherwise use 'no-session'
        const sessionId = this.sessionId || 'no-session';
        
        // Keep "connection" empty by default
        const logEntry = `accountId=${userAccountId}&message=${message}&source=Web App&connection=&type=${type}&method=${method}&file=${finalFileName}&time=${timestamp}&logId=${logId}&transactionId=${this.transactionId}&sessionId=${sessionId}&deviceId=${this.deviceId}`;
        
        return logEntry;
    }

    extractMethodFromStack(stack) {
        try {
            const match = (stack.split('\n').slice(3).find(l => l.includes('at ') && !l.includes('Logger.js')) || '').match(/at\s+(\w+)/);
            return match ? match[1] : 'anonymous';
        } catch (_) {
            return 'unknown';
        }
    }



        async sendToCloudWatch(logEntry) {
        if (!this.shouldLog()) { return; }

        // Add to buffer instead of sending immediately
        this.logBuffer += logEntry + '\n';
        
        
        // Flush buffer if it exceeds size limit
        if (this.logBuffer.length >= this.bufferSize) {
            this.flushBuffer();
        }
        
        // Trigger upload if we haven't uploaded recently (every 30 seconds)
        const now = Date.now();
        if (now - this.lastFlushTime > 30000) { // 30 seconds
            this.uploadLogFile();
        }
    }

    // MARK: - CloudWatch Upload

    async uploadLogFile() {
        if (!this.isConnectedToInternet()) { return; }

        // Only one upload can be in progress at a time
        if (this.isUploading || this.isUploadScheduled) { return; }

        this.isUploadScheduled = true;
        this.isUploading = true;

        try {
            const logContent = this.getLogFileContent();
            if (!logContent || logContent.trim() === '') { this.resetUploadState(); return; }

            await this.performUpload(logContent);
        } catch (error) {
            console.error('Error in uploadLogFile:', error);
            this.handleUploadError();
        }
    }

    async performUpload(logContent) {
        try {
            // Get the environment URL from Redux store (same as your other API calls)
            const envURL = this.getEnvironmentURL();
            if (!envURL) { throw new Error('Environment URL not available'); }

            const uploadURL = `${envURL.replace(/\/$/, '')}/upload/logs`; // Same endpoint as iOS: /upload/logs

            // Prepare the multipart form data for upload (matching iOS implementation)
            const formData = new FormData();
            
            // Append the logs as a file (matching iOS: "logFile")
            const logBlob = new Blob([logContent], { type: 'text/plain' });
            formData.append('logFile', logBlob, 'WebAppLogs.txt');
            
            // Append OS identifier (matching iOS: "os")
            formData.append('os', 'web');

            const headers = this.getAuthHeaders();

            const response = await fetch(uploadURL, {
                method: 'POST',
                headers: {
                    ...headers, // Get from your auth system
                },
                body: formData
            });

            if (response.ok) {
                this.handleUploadSuccess();
            } else {
                throw new Error(`Upload failed with status: ${response.status}`);
            }
        } catch (error) {
            console.error('Upload error:', error);
            throw error;
        }
    }

    handleUploadSuccess() {
        // Clear the log file after successful upload
        this.clearLogFile();
        
        // Write any cached logs that accumulated during upload
        if (this.tempLogCache) {
            this.writeLogToFile(this.tempLogCache);
            this.tempLogCache = '';
        }

        this.resetUploadState();
        this.uploadRetryCount = 0;
    }

    handleUploadError() {
        this.uploadRetryCount++;
        
        if (this.uploadRetryCount < this.maxRetries) {
            // Retry after exponential backoff
            const retryDelay = Math.pow(2, this.uploadRetryCount) * 1000;
            
            setTimeout(() => {
                this.isUploading = false;
                this.isUploadScheduled = false;
                this.uploadLogFile();
            }, retryDelay);
        } else {
            console.error('Max upload retries exceeded');
            this.resetUploadState();
            this.uploadRetryCount = 0;
        }
    }

    resetUploadState() {
        this.isUploading = false;
        this.isUploadScheduled = false;
    }

    getAuthHeaders() {
        // Get authentication headers from Redux store (same as iOS app)
        try {
            // Always try to get auth tokens if available
            if (this.authTokens) {
                return {
                    'authorization': `Bearer ${this.authTokens.authorization}`,
                    'refresh': `${this.authTokens.refresh}`,
                    'id': `${this.authTokens.id}`
                };
            }
            
            // Fallback: try to get from localStorage if Redux isn't available
            const token = localStorage.getItem('authToken') || '';
            if (token) {
                return { 'authorization': `Bearer ${token}` };
            }
            
            return {};
        } catch (error) {
            console.error('Error getting auth headers:', error);
            return {};
        }
    }

    isConnectedToInternet() {
        return navigator.onLine;
    }

    // Manual upload trigger for testing
    manualUpload() {
        this.uploadLogFile();
    }
    
    // Get current log buffer status for debugging
    getLogStatus() {
        return {
            bufferSize: this.logBuffer.length,
            totalLogs: this.getLogFileContent().length,
            pendingLogs: this.pendingLogs.length,
            isUploading: this.isUploading,
            lastFlushTime: this.lastFlushTime,
            environment: this.environment,
            logsOperational: this.logsOperational,
            hasAuthTokens: !!this.authTokens
        };
    }

    // Store environment URL when Logger is initialized
    setEnvironmentURL(envURL) {
        this.environmentURL = envURL;
    }

    getEnvironmentURL() {
        return this.environmentURL;
    }

    // Store auth tokens when they're set in the app (call this from your auth system)
    setAuthTokens(tokens) {
        this.authTokens = tokens;
    }
    
    // Set user ID for logging (separate from auth tokens)
    setUserId(userId) {
        this.userId = userId;
        // Generate new session ID when user logs in
        this.generateNewSessionId();
    }
    
    // Upload any pending logs when user logs in
    async uploadPendingLogs() {
        if (this.pendingLogs.length > 0) {
            const combinedLogs = this.pendingLogs.join('\n');
            
            // Add to buffer for immediate upload
            this.logBuffer += combinedLogs + '\n';
            
            // Clear pending logs
            this.pendingLogs = [];
            
            // Trigger immediate upload
            this.uploadLogFile();
        }
    }
    
    // Clear user ID when logging out
    clearUserId() {
        this.userId = null;
        // Clear session ID when logging out
        this.sessionId = null;
    }
    
    // Store a log entry in pending buffer
    storePendingLog(logEntry) {
        if (this.pendingLogs.length >= this.maxPendingLogs) {
            // Remove oldest log to make room
            this.pendingLogs.shift();
        }
        this.pendingLogs.push(logEntry);
    }

    // Sync with Redux store state (call this when tokens change)
    syncWithReduxStore(store) {
        if (store && store.getState) {
            const state = store.getState();
            if (state.userInfo && state.userInfo.tokens) {
                this.authTokens = state.userInfo.tokens;
            }
        }
    }

    // Update auth tokens (call this whenever tokens change in Redux)
    updateAuthTokens(tokens) {
        this.authTokens = tokens;
        // On receiving tokens, try to upload any buffered/pending logs immediately
        try { this.uploadPendingLogs(); } catch (_) {}
        try { this.uploadLogFile(); } catch (_) {}
    }
}

export default Logger;
